import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Camera, LogOut, Home, Music, Calendar, Users, Bell, FileText, CheckCircle2, Download, X, Image as ImageIcon } from "lucide-react";
import { useStudentAuth, formatApiErrorDetail } from "../context/StudentAuth";
import { Logo } from "../components/site/Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const DAY_ORDER = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const nextClassDate = (startDate, classDays) => {
  if (!startDate || !classDays?.length) return null;
  const days = new Set(classDays.map((d) => DAY_ORDER.indexOf(d)));
  const start = new Date(startDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cursor = new Date(start > today ? start : today);
  for (let i = 0; i < 14; i++) {
    if (days.has(cursor.getDay()) && cursor >= today) return new Date(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  return null;
};

const fmtDate = (d) => d?.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
const fmtShort = (s) => new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const fmtRel = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId} className="border border-[rgba(28,22,16,0.14)] bg-[#fefefc] p-4 sm:p-5 flex items-start gap-3">
    <div className="w-8 h-8 rounded-full bg-[#DB0909]/10 border border-[#DB0909]/30 flex items-center justify-center text-[#DB0909] shrink-0">
      <Icon size={13} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#4a3e30]">{label}</p>
      <p className="mt-0.5 font-display font-bold text-base sm:text-lg text-[#1c1610] leading-tight truncate">{value}</p>
    </div>
  </div>
);

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join("");

// Rotating watermark overlay — legally deters screenshot leaks
const Watermark = ({ user }) => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden select-none" aria-hidden>
    <div
      className="absolute inset-[-30%] flex flex-wrap items-center justify-center gap-x-16 gap-y-24 opacity-[0.12] rotate-[-24deg] text-[#DB0909] font-mono text-[11px] tracking-[0.35em] uppercase"
    >
      {Array.from({ length: 40 }).map((_, i) => (
        <span key={i}>ABC · {user.email} · {user.username}</span>
      ))}
    </div>
  </div>
);

const MaterialViewer = ({ material, user, token, onClose }) => {
  const [blobUrl, setBlobUrl] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    let currentUrl = "";
    axios.get(`${API}/api/students/materials/${material.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    }).then((r) => {
      currentUrl = URL.createObjectURL(r.data);
      setBlobUrl(currentUrl);
    }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    return () => { if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [material.id, token]);

  // Deterrents (best-effort — cannot truly block screenshots)
  useEffect(() => {
    const block = (e) => e.preventDefault();
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "c"].includes(e.key.toLowerCase())) e.preventDefault();
      if (e.key === "PrintScreen") e.preventDefault();
    };
    document.addEventListener("contextmenu", block);
    document.addEventListener("keydown", onKey);
    document.addEventListener("dragstart", block);
    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("dragstart", block);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-[#1c1610]/95 backdrop-blur-md flex flex-col" data-testid="material-viewer">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#f7f6f2]/10 bg-[#1c1610]">
        <div className="min-w-0">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#DB0909]">{material.kind === "pdf" ? "PDF" : "Image"} · Protected</p>
          <p className="font-display font-bold text-[#f7f6f2] truncate">{material.filename}</p>
        </div>
        <button
          onClick={onClose}
          data-testid="material-viewer-close"
          className="w-9 h-9 flex items-center justify-center border border-[#f7f6f2]/20 text-[#f7f6f2] hover:border-[#DB0909] hover:text-[#DB0909] transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>
      <div className="relative flex-1 overflow-hidden">
        {err && (
          <div className="absolute inset-0 flex items-center justify-center text-[#f7f6f2] font-mono text-xs tracking-[0.2em] uppercase">
            {err}
          </div>
        )}
        {!blobUrl && !err && (
          <div className="absolute inset-0 flex items-center justify-center text-[#f7f6f2]/60 font-mono text-[10px] tracking-[0.3em] uppercase">
            Loading…
          </div>
        )}
        {blobUrl && material.kind === "pdf" && (
          <iframe
            src={`${blobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            title={material.filename}
            className="w-full h-full bg-white"
            data-testid="material-viewer-frame"
          />
        )}
        {blobUrl && material.kind === "image" && (
          <div className="w-full h-full flex items-center justify-center p-6 bg-[#0e0a08]">
            <img
              src={blobUrl}
              alt={material.filename}
              className="max-w-full max-h-full object-contain pointer-events-none select-none"
              onDragStart={(e) => e.preventDefault()}
            />
          </div>
        )}
        <Watermark user={user} />
      </div>
      <div className="px-5 py-2 text-center font-mono text-[9px] tracking-[0.25em] uppercase text-[#f7f6f2]/40 bg-[#1c1610]">
        Distributing screenshots identifies your account · Personal viewing only
      </div>
    </div>
  );
};

const BatchCard = ({ res, materials, announcements, onOpen }) => {
  const batch = res.batch;
  const nextDate = batch ? nextClassDate(batch.start_date, batch.class_days) : null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="border border-[#1c1610]/25 bg-[#fefefc] p-5 sm:p-8 shadow-[6px_6px_0_rgba(28,22,16,0.12)]"
      data-testid={`profile-batch-card-${res.batch_id}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30]">Course</p>
          <h2 className="mt-1 font-display font-black text-2xl sm:text-3xl text-[#1c1610] leading-tight">{res.course_name}</h2>
        </div>
        <span className={`font-mono text-[10px] tracking-[0.25em] uppercase px-3 py-1.5 border ${
          batch?.status === "in_progress" ? "border-[#DB0909] bg-[#DB0909]/10 text-[#DB0909]"
          : batch?.status === "completed" ? "border-[rgba(28,22,16,0.3)] text-[#4a3e30]"
          : "border-[#1c1610] bg-[#1c1610] text-[#f7f6f2]"
        }`}>
          {batch?.status === "in_progress" ? "● Live" : batch?.status === "completed" ? "Completed" : "Upcoming"}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Music} label="Batch" value={batch?.name || res.batch_name} testId={`stat-batch-${res.batch_id}`} />
        <StatCard icon={Calendar} label={batch?.status === "upcoming" ? "Starts" : "Started"} value={batch ? fmtShort(batch.start_date) : "TBA"} testId={`stat-start-${res.batch_id}`} />
        <StatCard icon={Users} label="Class days" value={(batch?.class_days || []).join(" & ") || "TBA"} testId={`stat-days-${res.batch_id}`} />
        <StatCard icon={Calendar} label="Next class" value={nextDate ? fmtDate(nextDate) : "TBA"} testId={`stat-next-${res.batch_id}`} />
      </div>

      {batch?.sessions?.length ? (
        <details className="mt-8 group" open={materials.length === 0 && announcements.length === 0}>
          <summary
            data-testid={`classes-toggle-${res.batch_id}`}
            className="cursor-pointer flex items-center gap-3 flex-wrap py-3 px-4 border border-[rgba(28,22,16,0.14)] bg-[#f6efe3]/40 hover:border-[#DB0909] hover:bg-[#DB0909]/[0.04] transition-colors list-none [&::-webkit-details-marker]:hidden"
          >
            <span aria-hidden className="font-display font-black text-2xl text-[#DB0909] leading-none">♪</span>
            <span className="font-display font-black text-xl sm:text-2xl text-[#1c1610]">Classes done so far</span>
            <span className="font-mono text-[11px] font-bold tracking-[0.2em] uppercase bg-[#DB0909] text-[#f7f6f2] px-2 py-1">
              {batch.classes_completed || 0} / {batch.sessions.length}
            </span>
            <span className="ml-auto inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-[#950606] group-hover:text-[#DB0909] transition-colors">
              <span className="group-open:hidden">See what's covered</span>
              <span className="hidden group-open:inline">Hide list</span>
              <span aria-hidden className="inline-block transition-transform duration-200 group-open:rotate-180 text-sm">▾</span>
            </span>
          </summary>
          <div className="mt-1 h-[2px] bg-gradient-to-r from-[#DB0909] via-[#DB0909]/30 to-transparent" />
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2" data-testid={`sessions-${res.batch_id}`}>
            {batch.sessions.map((s, i) => {
              const done = i < (batch.classes_completed || 0);
              const isNext = i === (batch.classes_completed || 0);
              return (
                <li
                  key={i}
                  className={`flex items-center gap-3 py-2 border-b border-[rgba(28,22,16,0.08)] ${done ? "opacity-70" : ""}`}
                >
                  <span className={`font-mono text-[11px] font-bold w-8 shrink-0 ${done ? "text-[#DB0909]" : isNext ? "text-[#DB0909]" : "text-[#4a3e30]/70"}`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`flex-1 text-base ${
                      done
                        ? "line-through text-[#4a3e30] font-light"
                        : isNext
                          ? "text-[#1c1610] font-display font-bold"
                          : "text-[#1c1610] font-normal"
                    }`}
                  >
                    {s}
                  </span>
                  {done ? (
                    <CheckCircle2 size={16} className="text-[#DB0909] shrink-0" />
                  ) : isNext ? (
                    <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#DB0909] bg-[#DB0909]/10 border border-[#DB0909]/40 px-2 py-1 shrink-0">Up next</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}

      {/* Announcements */}
      {announcements.length > 0 && (
        <div className="mt-10" data-testid={`announcements-${res.batch_id}`}>
          <div className="flex items-baseline gap-3">
            <span aria-hidden className="font-display font-black text-2xl text-[#DB0909] leading-none">📣</span>
            <h3 className="font-display font-black text-xl sm:text-2xl text-[#1c1610]">From Sanjith</h3>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30]">
              {announcements.length} update{announcements.length > 1 ? "s" : ""}
            </span>
          </div>
          <div className="mt-1 h-[2px] bg-gradient-to-r from-[#DB0909] via-[#DB0909]/30 to-transparent" />
          <div className="mt-5 space-y-3">
            {announcements.map((a, i) => (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i, duration: 0.35 }}
                className="relative border-l-[3px] border-[#DB0909] bg-gradient-to-r from-[#DB0909]/[0.06] to-transparent pl-5 pr-5 py-4 rounded-r-sm"
              >
                {i === 0 && (
                  <span className="absolute -top-2 -left-1 font-mono text-[9px] font-bold tracking-[0.2em] uppercase bg-[#DB0909] text-[#f7f6f2] px-2 py-0.5">
                    New
                  </span>
                )}
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <h4 className="font-display font-black text-lg sm:text-xl text-[#1c1610] leading-tight">{a.title}</h4>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#950606] shrink-0">{fmtRel(a.created_at)}</span>
                </div>
                <p className="mt-2 text-sm sm:text-base text-[#1c1610]/85 font-light leading-relaxed whitespace-pre-wrap">{a.body}</p>
              </motion.article>
            ))}
          </div>
        </div>
      )}

      {/* Materials */}
      <div className="mt-10" data-testid={`materials-${res.batch_id}`}>
        <div className="flex items-baseline gap-3">
          <span aria-hidden className="font-display font-black text-2xl text-[#DB0909] leading-none">🎼</span>
          <h3 className="font-display font-black text-xl sm:text-2xl text-[#1c1610]">Practice Kit</h3>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30]">
            {materials.length === 0 ? "coming soon" : `${materials.length} file${materials.length > 1 ? "s" : ""}`}
          </span>
        </div>
        <div className="mt-1 h-[2px] bg-gradient-to-r from-[#DB0909] via-[#DB0909]/30 to-transparent" />
        {materials.length === 0 ? (
          <div className="mt-4 border border-dashed border-[rgba(28,22,16,0.2)] bg-[#f6efe3]/30 p-5 sm:p-6">
            <p className="text-sm sm:text-base text-[#1c1610]/85 font-light leading-relaxed">
              <span className="font-display font-bold text-[#1c1610]">Sanjith will drop your practice files here</span> — tabs, chord sheets, and reference PDFs land on this page as your batch moves forward.
            </p>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {materials.map((m, i) => (
              <motion.button
                key={m.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * i, duration: 0.35 }}
                data-testid={`material-open-${m.id}`}
                onClick={() => onOpen(m)}
                className="text-left border border-[rgba(28,22,16,0.15)] bg-[#fefefc] hover:border-[#DB0909] hover:shadow-[4px_4px_0_rgba(219,9,9,0.15)] hover:-translate-y-0.5 p-4 sm:p-5 transition-all group"
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`w-12 h-12 flex items-center justify-center shrink-0 rounded-sm ${
                      m.kind === "pdf"
                        ? "bg-[#DB0909] text-[#f7f6f2]"
                        : "bg-[#1c1610] text-[#f7f6f2]"
                    }`}
                  >
                    {m.kind === "pdf" ? <FileText size={20} /> : <ImageIcon size={20} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-bold text-base sm:text-lg text-[#1c1610] leading-tight group-hover:text-[#DB0909] transition-colors line-clamp-2">
                      {m.filename}
                    </p>
                    <p className="mt-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">
                      {m.kind === "pdf" ? "PDF" : "Image"} · {(m.size / 1024).toFixed(0)}KB
                    </p>
                    <p className="mt-0.5 font-mono text-[10px] tracking-[0.15em] uppercase text-[#950606]">
                      Uploaded {fmtRel(m.uploaded_at)}
                    </p>
                  </div>
                </div>
                <p className="mt-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#DB0909] opacity-0 group-hover:opacity-100 transition-opacity">
                  Open →
                </p>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </motion.section>
  );
};

export default function Profile() {
  const nav = useNavigate();
  const { user, reservations, checked, logout, refresh, updateProfile, authHeaders } = useStudentAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [materials, setMaterials] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [openMaterial, setOpenMaterial] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (checked && !user) nav("/login?from=/profile", { replace: true });
  }, [user, checked, nav]);

  useEffect(() => {
    if (user) refresh();
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      axios.get(`${API}/api/students/materials`, { headers: authHeaders() }).then((r) => r.data).catch(() => []),
      axios.get(`${API}/api/students/announcements`, { headers: authHeaders() }).then((r) => r.data).catch(() => []),
    ]).then(([m, a]) => { setMaterials(m); setAnnouncements(a); });
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleFile = async (e) => {
    setUploadErr("");
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpeg|jpg)$/i.test(f.type)) { setUploadErr("Please upload a PNG or JPEG image."); return; }
    if (f.size > 500 * 1024) { setUploadErr("Image too large — please pick something under 500KB."); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try { await updateProfile({ profile_image: reader.result }); }
      catch (err) { setUploadErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
      finally { setUploading(false); }
    };
    reader.onerror = () => { setUploadErr("Could not read image file."); setUploading(false); };
    reader.readAsDataURL(f);
  };

  const removePic = async () => {
    setUploading(true);
    try { await updateProfile({ profile_image: "" }); } finally { setUploading(false); }
  };

  const matsByBatch = useMemo(() => {
    const m = {};
    materials.forEach((x) => { (m[x.batch_id] ||= []).push(x); });
    return m;
  }, [materials]);

  const anncByBatch = useMemo(() => {
    const m = {};
    announcements.forEach((x) => { (m[x.batch_id] ||= []).push(x); });
    return m;
  }, [announcements]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#f7f6f2] flex items-center justify-center">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4a3e30]">Loading…</p>
      </div>
    );
  }

  const token = localStorage.getItem("abc_student_token") || "";

  return (
    <div className="min-h-screen bg-[#f7f6f2] grain relative overflow-hidden student-profile">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 55% 40% at 82% 8%, rgba(219,9,9,0.08), transparent 65%), radial-gradient(ellipse 45% 40% at 12% 92%, rgba(219,9,9,0.05), transparent 65%)" }}
      />

      <nav className="relative z-20 border-b border-[rgba(28,22,16,0.12)] bg-[#f7f6f2]/85 backdrop-blur-md">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="profile-home-link" className="flex items-center gap-3 group" title="Back to abc guitar lessons">
            <Logo className="h-10 md:h-12" />
            <span className="hidden sm:inline font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30] group-hover:text-[#DB0909] transition-colors">abc guitar lessons</span>
          </Link>
          <button
            data-testid="profile-logout-btn"
            onClick={() => { logout(); nav("/"); }}
            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30] hover:text-[#DB0909] transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </nav>

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14">
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8"
          data-testid="profile-header"
        >
          <div className="relative shrink-0">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-2 border-[#1c1610] bg-[#f6efe3] overflow-hidden shadow-[6px_6px_0_rgba(28,22,16,0.15)]">
              {user.profile_image ? (
                <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#DB0909] to-[#950606] text-[#f7f6f2] font-display font-black text-3xl sm:text-4xl">
                  {initials(user.name) || <Music size={32} />}
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="profile-avatar-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[#1c1610] border-2 border-[#f7f6f2] text-[#f7f6f2] flex items-center justify-center hover:bg-[#DB0909] transition-colors disabled:opacity-60"
              aria-label="Upload profile picture"
            >
              <Camera size={13} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFile}
              className="hidden"
              data-testid="profile-avatar-file"
            />
            <span aria-hidden className="absolute -top-2 -left-3 rotate-[-14deg] font-display text-2xl text-[#DB0909]/70 select-none">♪</span>
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#950606]" data-testid="profile-role-kicker">
              Student · @{user.username}
            </p>
            <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl md:text-5xl text-[#1c1610] leading-none truncate" data-testid="profile-name">{user.name}</h1>
            <p className="mt-2 text-sm text-[#4a3e30] font-light break-all" data-testid="profile-email">{user.email}</p>
            {user.profile_image && (
              <button
                onClick={removePic}
                data-testid="profile-avatar-remove-btn"
                className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-[#950606] hover:text-[#DB0909] underline underline-offset-4"
              >
                Remove picture
              </button>
            )}
            {uploadErr && <p data-testid="profile-upload-error" className="mt-2 font-mono text-[10px] tracking-[0.15em] uppercase text-[#DB0909]">{uploadErr}</p>}
          </div>
        </motion.section>

        {/* All batches (a student can have multiple) */}
        {reservations.length > 0 ? (
          <div className="mt-12">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-[#DB0909] animate-pulse" />
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#950606]">Your courses · {reservations.length}</p>
            </div>
            <div className="mt-6 space-y-10">
              {reservations.map((r, i) => (
                <div key={r.id}>
                  {i > 0 && (
                    <div className="flex items-center gap-4 py-6" aria-hidden>
                      <span className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-[rgba(28,22,16,0.25)] to-transparent" />
                      <span className="font-display text-2xl text-[#DB0909] tracking-widest select-none">♪ ♫ ♪</span>
                      <span className="h-[1px] flex-1 bg-gradient-to-r from-transparent via-[rgba(28,22,16,0.25)] to-transparent" />
                    </div>
                  )}
                  <BatchCard
                    res={r}
                    materials={matsByBatch[r.batch_id] || []}
                    announcements={anncByBatch[r.batch_id] || []}
                    onOpen={setOpenMaterial}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-12 border border-dashed border-[rgba(28,22,16,0.25)] bg-[#fefefc] p-8 md:p-12 text-center"
            data-testid="profile-no-course"
          >
            <Music size={28} className="text-[#DB0909] mx-auto" />
            <h2 className="mt-4 font-display font-black text-2xl text-[#1c1610]">No course yet — the stage is empty.</h2>
            <p className="mt-2 text-sm text-[#4a3e30] font-light max-w-sm mx-auto">
              Book a course with the same email <span className="text-[#1c1610]">({user.email})</span> and it'll auto-link here.
            </p>
            <Link
              to="/#courses"
              data-testid="profile-book-cta"
              className="mt-6 inline-block font-mono text-xs tracking-[0.3em] uppercase bg-[#1c1610] text-[#f7f6f2] px-8 py-4 hover:bg-[#DB0909] transition-colors"
            >
              Choose your course →
            </Link>
          </motion.section>
        )}
      </div>

      {openMaterial && (
        <MaterialViewer
          material={openMaterial}
          user={user}
          token={token}
          onClose={() => setOpenMaterial(null)}
        />
      )}
    </div>
  );
}
